import React from 'react'

import {
  classnames,
  Icon,
  ArrowRight,
  Radio,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, number, string, func, oneOf, arrayOf } from 'prop-types'

import SubscriptionEntryPoints from 'Pods/Shared/Constants/subscriptionEntryPoints'
import { setSubscriptionEntry } from 'Pods/Shared/Utils/subscriptionEntry'

import roundHumanCount from 'Utils/roundHumanCount'
import { EMPTY_ARRAY, NOOP } from 'Utils/stableDefaults'

import PlanDetails from './Components/PlanDetails/PlanDetails'
import { subscriptionPlanShape } from '../../../Utils/propTypes'
import translatePlansRestructure from '../../../Utils/translatePlansRestructure'

const BAND_PREMIUM = 'premium'

function SubscriptionOption({
  selected,
  onSelect: handleSelect,
  onNavigate: handleNavigate = NOOP,
  planName,
  band = 'essentials',
  bullets = EMPTY_ARRAY,
  downloadsPerMonth,
  catalogValue,
  pricePerModel,
  perModelLabel,
  fromLabel,
  plan = null,
  plansRestructureEnabled = false,
}) {
  const hasBackendBullets = Array.isArray(bullets) && bullets.length > 0
  const showLearnMore = !plan?.isUpgrade && (plansRestructureEnabled || band !== BAND_PREMIUM)

  // stopPropagation keeps the surrounding option from being selected by this link.
  const handleLearnMoreClick = (event) => {
    event.stopPropagation()
    setSubscriptionEntry(SubscriptionEntryPoints.LANDING_LEARN_MORE)
  }
  const restructureClass = (base) => classnames(base, plansRestructureEnabled && `${base}--restructure`)
  const itemClass = restructureClass('subscription-purchase__benefits-item')
  const optionClass = classnames(
    restructureClass('subscription-purchase__option'),
    'subscription-purchase__option--subscription',
    selected && 'subscription-purchase__option--selected'
  )

  const legacyDetails = (
    <>
      <div className={restructureClass('subscription-purchase__option-header')}>
        <span className="subscription-purchase__option-title">{planName}</span>
        <div className={restructureClass('subscription-purchase__option-price')}>
          <div className="subscription-purchase__option-price-row">
            <span className="cgt-body-base subscription-purchase__from-prefix">
              {fromLabel}
            </span>
            <span className="subscription-purchase__current-price">{pricePerModel}</span>
          </div>
          <span className="cgt-caption-semibold subscription-purchase__per-model-label">
            {`/${perModelLabel}`}
          </span>
        </div>
      </div>
      <ul className={restructureClass('subscription-purchase__benefits-list')}>
        {hasBackendBullets ? (
          bullets.map((text) => (
            <li key={text} className={itemClass}>{text}</li>
          ))
        ) : (
          <>
            <li className={itemClass}>Designed to meet your everyday needs</li>
            <li className={itemClass}>
              Download up to
              {' '}
              <strong className="subscription-purchase__benefits-highlight">
                {downloadsPerMonth} models
              </strong>
              {' '}
              per month
            </li>
            <li className={itemClass}>
              Access a library of
              {' '}
              <strong className="subscription-purchase__benefits-highlight">{roundHumanCount(catalogValue)}</strong>
              {' '}
              premium models
            </li>
          </>
        )}
      </ul>
    </>
  )

  return (
    <div
      className={optionClass}
      onClick={handleSelect}
      onKeyDown={(e) => {
        // Keys bubbling from the nested Learn more link must keep their native behaviour.
        if (e.currentTarget !== e.target) return

        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleSelect()
          return
        }

        handleNavigate(e)
      }}
      role="radio"
      aria-checked={selected}
      tabIndex={selected ? 0 : -1}
    >
      <div className="subscription-purchase__option-radio" aria-hidden="true">
        <Radio
          name="purchaseOption"
          isChecked={selected}
          onChange={handleSelect}
          className="subscription-purchase__radio"
        />
      </div>

      <div className={restructureClass('subscription-purchase__option-content')}>
        {plan ? (
          <PlanDetails
            plan={plan}
            band={band}
            downloadsPerMonth={downloadsPerMonth}
            pricePerModel={pricePerModel}
            fromLabel={fromLabel}
            perModelLabel={perModelLabel}
          />
        ) : legacyDetails}
        {showLearnMore && (
          <a
            href="/subscription"
            className={plansRestructureEnabled
              ? 'subscription-purchase__plan-learn-more cgt-link-primary'
              : 'subscription-purchase__learn-more'}
            data-subscription-entry={SubscriptionEntryPoints.LANDING_LEARN_MORE}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleLearnMoreClick}
          >
            {plansRestructureEnabled ? translatePlansRestructure('learn_more') : 'Learn more'}
            <Icon
              svgComponent={ArrowRight}
              size="small"
              className={plansRestructureEnabled ? 'cgt-link__icon' : undefined}
            />
          </a>
        )}
      </div>
    </div>
  )
}

SubscriptionOption.propTypes = {
  selected: bool.isRequired,
  onSelect: func.isRequired,
  onNavigate: func,
  planName: string.isRequired,
  band: oneOf(['essentials', 'premium']),
  bullets: arrayOf(string),
  downloadsPerMonth: number.isRequired,
  catalogValue: string.isRequired,
  pricePerModel: string.isRequired,
  perModelLabel: string.isRequired,
  fromLabel: string.isRequired,
  plan: subscriptionPlanShape,
  plansRestructureEnabled: bool,
}

export default SubscriptionOption
